import groupBy from 'lodash-es/groupBy'
import SETTINGS from '@/src/settings'
import { formatProduct } from '@/lib/formatProduct'

// Reproduit fidelement la logique du resolver GraphQL searchProducts
// (apollo/server/resolvers/Catalog/searchProducts.ts) cote front, pour la
// migration vers TanStack Query / REST (lot 7).

const sortPriorityVendor = (raw: any[]) => {
  const priorityVendor = raw?.find(
    (i: any) => i.variant_id === SETTINGS.attributesCodeUse.compagnyIdPharmedistore
  )
    ? [raw?.find((i: any) => i.variant_id === SETTINGS.attributesCodeUse.compagnyIdPharmedistore)]
    : []
  return [
    ...priorityVendor,
    ...raw.filter((i: any) => i.variant_id !== SETTINGS.attributesCodeUse.compagnyIdPharmedistore),
  ]
}

export type SearchProductsArgs = {
  query?: string
  q?: string
  queryFacet?: string
  page?: number
  sortBy?: string
  sortOrder?: string
  resultsPerPage?: string
  featuresHash?: Array<{ filter_id?: string; value?: string }>
  filters?: Array<{ label?: string; value?: string }>
  filtersQuery?: Array<{ label?: string; value?: string }>
  alsoBoughtForProductIds?: string[]
  getFilters?: boolean
  displayProductLastPurchase?: boolean
  displayProductLastPurchaseLastFourWeek?: boolean
  disabledAlgolia?: boolean
  rawFacets?: boolean
  // Blocs de recommandation servis par la recherche (meilleures ventes, co-achats) :
  // le back retire les marques cochees « Exclue des recommandations ». Jamais pour
  // la recherche ni la navigation, ou ces marques doivent rester trouvables.
  excludeRecommendBrands?: boolean
}

// Construit les params REST envoyes au proxy CS-Cart (endpoint `products`).
// Reproduit la construction du resolver (features_hash / raw_facet_filters /
// filter_variants / get_only_filters / display_product_last_purchase / etc).
// Retourne un objet brut (potentiellement imbrique : filter_variants, arrays)
// destine a etre serialise via qs.stringify (comme le resolver via callCsCartApi).
export const buildSearchParams = (args: SearchProductsArgs): Record<string, unknown> => {
  const {
    query = '',
    q,
    queryFacet = '',
    page = 1,
    sortBy,
    sortOrder,
    resultsPerPage = '1',
    featuresHash = [],
    filters = [],
    filtersQuery = [],
    alsoBoughtForProductIds = [],
    getFilters = false,
    displayProductLastPurchase = false,
    displayProductLastPurchaseLastFourWeek = false,
    disabledAlgolia = false,
    rawFacets = false,
    excludeRecommendBrands = false,
  } = args

  const featuresHashGroupById = groupBy(featuresHash, (i: any) => i.filter_id)

  // filter
  const filtersFormatted: Record<string, unknown> = {}
  filters.forEach((line: any) => {
    filtersFormatted[line.label] = line.value.includes('{') ? JSON.parse(line.value) : line.value
  })

  // Detecter si les filtres sont des facettes du moteur (noms textuels) ou CS-Cart (IDs numeriques)
  const filterIds = Object.keys(featuresHashGroupById)
  const hasEngineFacets = filterIds.some(id => isNaN(Number(id)))

  // Pour les facettes du moteur: envoyer via raw_facet_filters
  // Pour CS-Cart: envoyer via features_hash
  const facetHashFormatted: string[] = hasEngineFacets
    ? []
    : Object.keys(featuresHashGroupById).map(
        filterId =>
          `${filterId}-${featuresHashGroupById[filterId].map((i: any) => i.value).join('-')}`
      )

  const rawFacetFilters: Record<string, string[]> | null = hasEngineFacets
    ? Object.keys(featuresHashGroupById).reduce(
        (acc, filterId) => {
          acc[filterId] = featuresHashGroupById[filterId].map((i: any) => i.value)
          return acc
        },
        {} as Record<string, string[]>
      )
    : null

  const filtersQueryFormatted: Record<string, unknown> = {}
  filtersQuery.forEach((line: any) => {
    filtersQueryFormatted[line.label] = line.value.includes('{')
      ? JSON.parse(line.value)
      : line.value
  })

  // Ajouter raw_facet_filters si on a des facettes du moteur
  if (rawFacetFilters) {
    filtersQueryFormatted['raw_facet_filters'] = JSON.stringify(rawFacetFilters)
  }

  const get_filters = getFilters ? { get_only_filters: true } : {}
  const display_product_last_purchase = displayProductLastPurchase
    ? { display_product_last_purchase: true }
    : displayProductLastPurchaseLastFourWeek
      ? { display_product_purchase_after_four_week: true }
      : {}

  const disabled_algolia = disabledAlgolia ? { disabled_algolia: true } : {}
  const raw_facets = rawFacets ? { raw_facets: true } : {}
  const exclude_recommend_brands = excludeRecommendBrands ? { exclude_recommend_brands: 'Y' } : {}

  return {
    query,
    q,
    queryFacet,
    sort_by: sortBy,
    sort_order: sortOrder,
    ...get_filters,
    page,
    items_per_page: resultsPerPage,
    also_bought_for_product_ids: alsoBoughtForProductIds,
    filter_variants: { ...filtersFormatted },
    features_hash: facetHashFormatted.join('_'),
    ...filtersQueryFormatted,
    ...display_product_last_purchase,
    ...disabled_algolia,
    ...raw_facets,
    ...exclude_recommend_brands,
  }
}

// Reproduit la transfo de sortie du resolver (params.nbPages, nettoyage filters,
// discount, products.map(formatProduct)).
export const formatSearchResult = (result: any, getFilters = false): any => {
  if (!result) {
    return {
      params: { total_items: 0, nbPages: 0, page: 0, items_per_page: 0 },
      products: [],
      filters: [],
      discount: false,
    }
  }

  const numberResultPageNew = 144

  result.params = result.params
    ? {
        ...result.params,
        total_items: result.params.total_items,
        nbPages: Math.ceil(numberResultPageNew / result.params.items_per_page),
      }
    : {
        total_items: 0,
        nbPages: 0,
        page: 0,
        items_per_page: 0,
      }

  // On nettoie le retour des facettes pour avoir un schema plus propre
  result.filters =
    getFilters && result.filters && result.filters.length
      ? result.filters?.map((oneFacet: any) => {
          const min = oneFacet?.min ? { min: String(oneFacet.min) } : {}
          const max = oneFacet?.max ? { max: String(oneFacet.max) } : {}

          return {
            ...oneFacet,
            ...min,
            ...max,
            variants:
              oneFacet.filter_id === SETTINGS.idFiltersFacet.vendorId
                ? sortPriorityVendor(oneFacet.variants)
                : oneFacet.variants,
          }
        })
      : []

  result.discount = !!result.products
    ? !!result.products.find(
        (p: any) =>
          parseFloat(p.price) != parseFloat(p.base_price) ||
          parseFloat(p.list_price) ||
          (p?.prices_current && p.prices_current.length > 1)
      )
    : false

  result.products = !!result.products ? result.products.map(formatProduct) : []

  return result
}
